import { useMemo } from "react";
import { channelColor } from "../channelColors.js";
import type { UseProgramMapResult } from "../useProgramMap.js";

export type ChannelChipProps = {
  channel: number;
  used: boolean;
  active: boolean;
  noteCount: number;
  /** GM-программа канала (см. `channelProgram`). Для канала 9 — всегда 0. */
  program: number;
  programMap: UseProgramMapResult;
  onToggle: (channel: number) => void;
  onInstrumentChange: (channel: number, program: number) => void;
};

const label = (ch: number): string => (ch === 9 ? "9 Drums" : `ch ${ch}`);

/**
 * Контейнер одного канала в аккордионе «Каналы»: кружок с цветом канала +
 * toggle-переключатель + редактируемый селектор инструмента из текущего
 * soundfont. Канал 9 (ударные) — не редактируется: вместо селектора
 * показывается имя drum-кита только для чтения.
 */
export const ChannelChip = ({
  channel,
  used,
  active,
  noteCount,
  program,
  programMap,
  onToggle,
  onInstrumentChange,
}: ChannelChipProps) => {
  const currentName = useMemo(
    () => programMap.melodic.find((m) => m.program === program)?.name ?? "",
    [programMap.melodic, program]
  );

  const handleChange = (name: string) => {
    const prog = programMap.reverse.get(name);
    if (prog !== undefined) onInstrumentChange(channel, prog);
  };

  const stopProp = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div className="pr-channels__chip" data-channel={channel} data-testid={`channel-chip-${channel}`}>
      <button
        type="button"
        className={`chip ${active ? "chip--active" : ""}`}
        onClick={() => onToggle(channel)}
        disabled={!used}
        aria-pressed={active}
      >
        <span
          className="chip__dot"
          style={{ background: channelColor(channel) }}
          aria-hidden="true"
        />
        {label(channel)}
        {used ? null : " (пусто)"}
      </button>
      {channel === 9 ? (
        <span
          className="chip__drum"
          data-testid="chip-drum-kit"
          title={`Ударные: ${programMap.drumKit ?? "не найдены"}`}
        >
          🥁 {programMap.loading ? "…" : (programMap.drumKit ?? "—")}
        </span>
      ) : (
        <select
          className="chip__select"
          value={currentName}
          disabled={!used || programMap.loading}
          title={`Инструмент канала ${channel} (GM program ${program})`}
          aria-label={`Инструмент канала ${channel}`}
          onClick={stopProp}
          onChange={(e) => handleChange(e.target.value)}
          data-testid="chip-instrument-select"
        >
          {currentName ? null : <option value="">—</option>}
          {programMap.melodic.map((m) => (
            <option key={m.program} value={m.name}>
              {m.name}
            </option>
          ))}
        </select>
      )}
      <span className="chip__count muted">{noteCount}</span>
    </div>
  );
};
